import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { SimpleEditor } from "@/components/tiptap-templates/simple/simple-editor";
import { usePostService } from "../services/postService";
import { useLanguage } from "../providers/languageProvider";
import { PostCategory, LanguageCode } from "../types/post";

interface PostFormData {
  title: string;
  subtitleTranslations: Record<LanguageCode, string>;
  content: string;
  is_public: boolean;
  category_ids: string[];
}

export const NewPostPage = () => {
  const navigate = useNavigate();
  const postService = usePostService();
  const { availableLanguages } = useLanguage();

  const [formData, setFormData] = useState<PostFormData>({
    title: "",
    subtitleTranslations: { en: "", pl: "", uk: "" },
    content: "",
    is_public: false,
    category_ids: [],
  });

  const [editorHtml, setEditorHtml] = useState<string>("");
  const [categories, setCategories] = useState<PostCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const categoriesData = await postService.getPostCategories();
      setCategories(categoriesData);
    } catch (error) {
      console.error("Error loading categories:", error);
      alert("Nie udało się załadować kategorii");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      const submitData = {
        title: formData.title,
        subtitleTranslations: formData.subtitleTranslations,
        content: editorHtml,
        is_public: formData.is_public,
        category_ids: formData.category_ids,
      };
      await postService.createPostWithTranslations(submitData);
      navigate("/post");
    } catch (error) {
      console.error("Error creating post:", error);
      alert("Nie udało się utworzyć postu");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/post");
  };

  const updateSubtitleTranslation = (language: LanguageCode, value: string) => {
    setFormData((prev) => ({
      ...prev,
      subtitleTranslations: { ...prev.subtitleTranslations, [language]: value },
    }));
  };

  if (loading) {
    return <div className="p-6 text-center">Ładowanie kategorii...</div>;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="m-0 mb-2 text-blue-500 text-2xl font-bold">
          Utwórz Nowy Post
        </h1>
        <button
          onClick={handleCancel}
          className="px-3 py-1.5 bg-gray-50 text-gray-600 border border-gray-200 rounded cursor-pointer text-sm hover:bg-gray-100 transition-colors"
        >
          ← Powrót do listy postów
        </button>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-md">
        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <label className="block mb-2 font-medium text-base text-gray-700">
              Tytuł:
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              required
              className="w-full p-3 border border-gray-300 rounded-md text-base box-border focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Wprowadź tytuł postu"
            />
          </div>

          <div className="mb-6">
            <label className="block mb-3 font-medium text-base text-gray-700">
              Podtytuł (w różnych językach):
            </label>
            {availableLanguages.map((lang) => (
              <div key={`subtitle-${lang.code}`} className="mb-3">
                <label className="block mb-1 text-sm font-medium text-gray-600">
                  {lang.flag} {lang.name}
                </label>
                <input
                  type="text"
                  value={formData.subtitleTranslations[lang.code]}
                  onChange={(e) =>
                    updateSubtitleTranslation(lang.code, e.target.value)
                  }
                  className="w-full p-3 border border-gray-300 rounded-md text-base box-border focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={`Wprowadź podtytuł w języku ${lang.name.toLowerCase()}`}
                />
              </div>
            ))}
          </div>

          <div className="mb-6">
            <label className="block mb-2 font-medium text-base text-gray-700">
              Treść:
            </label>
            <div className="border border-gray-300 rounded-md min-h-[400px]">
              <SimpleEditor
                initialContent={editorHtml}
                onChange={(html) => {
                  setEditorHtml(html);
                  setFormData((prev) => ({ ...prev, content: html }));
                }}
              />
            </div>
          </div>

          <div className="mb-6">
            <label className="block mb-2 font-medium text-base text-gray-700">
              Kategorie:
            </label>
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <label
                  key={category.id}
                  className={`flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-md cursor-pointer text-sm transition-colors ${
                    formData.category_ids.includes(category.id)
                      ? "bg-blue-50 border-blue-200"
                      : "bg-gray-50 hover:bg-gray-100"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={formData.category_ids.includes(category.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData({
                          ...formData,
                          category_ids: [...formData.category_ids, category.id],
                        });
                      } else {
                        setFormData({
                          ...formData,
                          category_ids: formData.category_ids.filter(
                            (id) => id !== category.id
                          ),
                        });
                      }
                    }}
                    className="m-0"
                  />
                  {category.name}
                </label>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <label className="flex items-center gap-2 cursor-pointer text-base">
              <input
                type="checkbox"
                checked={formData.is_public}
                onChange={(e) =>
                  setFormData({ ...formData, is_public: e.target.checked })
                }
                className="rounded"
              />
              <span className="font-medium text-gray-700">
                Ustaw post jako publiczny
              </span>
            </label>
          </div>

          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-3 bg-gray-100 text-gray-600 border border-gray-300 rounded-md cursor-pointer text-base hover:bg-gray-200 transition-colors"
            >
              Anuluj
            </button>
            <button
              type="submit"
              disabled={submitting}
              className={`px-6 py-3 text-white border-none rounded-md text-base transition-colors ${
                submitting
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-600 cursor-pointer hover:bg-blue-700"
              }`}
            >
              {submitting ? "Tworzenie..." : "Utwórz Post"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
